import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { logEvent, analytics } from '../firebase.js';
import { DesignService } from '../services/design-service.js';
import { ProfileService } from '../services/profile-service.js';
import { QueryCache } from '../services/cache.js';
import { buildShoppingUrl, shoppingProvider } from '../services/shopping-links.js';
import { shareLink } from '../services/share-service.js';
import { useLocale, LANG_LABELS } from '../hooks/useLocale.jsx';
import { useInAppBack } from '../hooks/useInAppBack.jsx';
import { ReportModal } from '../components/ReportModal.jsx';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider.jsx';
import { PlanRoomResult, planSliderLabels, planPrefill } from '../components/PlanRoomResult.jsx';
import { Comments } from '../components/Comments.jsx';
import { GetTheAppBanner } from '../components/GetTheAppBanner.jsx';
import { AppGateModal } from '../components/AppGateModal.jsx';
import { useAppGate } from '../hooks/useAppGate.jsx';

export function ShareView({ user, openSignIn }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const back = useInAppBack();
  const { t, lang, styleName } = useLocale();
  const appGate = useAppGate('share');
  // Hydrate from cache (shared with DesignDetail under the same `design:{id}`
  // key) so navigating between owner view and share view is instant.
  const cachedDesign = id ? QueryCache.get(`design:${id}`) : null;
  const [design, setDesign] = useState(cachedDesign || null);
  const [loading, setLoading] = useState(!cachedDesign);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [author, setAuthor] = useState(null);

  useEffect(() => {
    DesignService.getDesign(id)
      .then(d => {
        setDesign(d);
        if (d) QueryCache.set(`design:${id}`, d);
      })
      .catch(() => { if (!cachedDesign) setDesign(null); })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (design) {
      setLikeCount(design.likeCount || 0);
      setLiked(!!(user && !user.isAnonymous && design.likedBy?.includes(user.uid)));
    }
  }, [design, user]);

  // Fetch the designer's public profile so we can show their handle / avatar
  // and link to their /u/{handle} page.
  useEffect(() => {
    if (!design?.userId || design.userId === 'anonymous') { setAuthor(null); return; }
    let cancelled = false;
    ProfileService.getByUid(design.userId).then(p => {
      if (!cancelled) setAuthor(p);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [design?.userId]);

  if (loading) return (
    <div className="share-page">
      <main className="main"><div className="loading"><div className="spinner"></div><p>{t('loading')}</p></div></main>
    </div>
  );

  // isPublic / isListed 게이트 제거 — 공개 피드를 접으면서 두 플래그를 켜는
  // UI 가 모두 사라졌고, 프로필 그리드는 이제 소유자의 디자인을 전부 보여준다.
  // 게이트를 남겨두면 프로필에서 카드를 눌렀을 때 자기 작업물조차 "비공개"
  // 화면이 뜬다. 애초에 보안 경계가 아니었다 — designs 는 firestore.rules 에서
  // `allow read: if true` 라 ID 만 알면 누구나 읽을 수 있었다.
  // 여기 오는 건 "비공개" 가 아니라 **문서를 못 찾았다** 는 뜻이다 (위 주석대로
  // 공개 게이트는 없앴다). 자물쇠 + "소유자가 공개하지 않았습니다" 는 사실과
  // 달라서, 공유한 사람이 "내가 공개를 안 했나" 하고 엉뚱한 데를 보게 된다.
  if (!design) return (
    <div className="share-page">
      <main className="main">
        <div className="share-private">
          <i className="material-symbols-rounded">link_off</i>
          <h2>{t('linkNotFoundTitle')}</h2>
          <p>{t('linkNotFoundDesc')}</p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>{t('tryApp')}</button>
        </div>
      </main>
    </div>
  );

  const handleLike = async () => {
    if (!user || user.isAnonymous) {
      openSignIn?.();
      return;
    }
    const next = !liked;
    setLiked(next);
    setLikeCount(c => c + (next ? 1 : -1));
    try {
      await DesignService.toggleLike(id, user.uid, liked);
    } catch {
      setLiked(!next);
      setLikeCount(c => c + (next ? -1 : 1));
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = design.name || `archelier — ${styleName(design.style, design.spaceCategory)}`;
    const shared = await shareLink({ title, url });
    if (shared) return;
    try {
      alert(t('copied'));
    } catch {
      // fall through silently
    }
  };

  const handleUseStyle = () => {

    // 디자인을 **보는 것**은 웹에 두고, 그 다음 행동에서 앱으로 보낸다.

    // 여기까지 온 사람은 이미 "나도 해볼까" 가 생겨 설치 이유가 분명하다.

    if (appGate.gate('use_style')) return;

    goCreateWithStyle();

  };


  // "Create with archelier" — 스타일 없이 그냥 만들러 가기. handleUseStyle 과
  // 같은 게이트를 태운다.
  const handleCreateWithApp = () => {
    if (appGate.gate('create_with_app')) return;
    navigate(window.matchMedia('(max-width: 768px)').matches ? '/create' : '/');
  };

  const goCreateWithStyle = () => {
    // 디자인을 **보는 것**은 웹에서 그대로 두고, 그 다음 행동에서 앱으로 보낸다.
    // 여기까지 온 사람은 이미 "나도 해볼까" 가 생겨 있어 설치 이유가 분명하다.
    // '/' 는 데스크탑에선 HomePage, 모바일에선 '/create' 로 redirect — 어느 쪽이든
    // 업로드 화면을 정확히 가리키려면 '/create' 를 직접 쓴다.
    const dest = window.matchMedia('(max-width: 768px)').matches ? '/create' : '/';
    navigate(dest, {
      state: {
        style: design.style,
        customStyle: design.customStyleInput || '',
        additionalRequest: design.additionalRequest || '',
        budget: design.budget || 'mid',
        mode: design.mode || 'redesign',
        spaceCategory: design.spaceCategory || 'interior',
        exteriorOptions: design.exteriorOptions || null,
        // Reference Style 디자인 — reference 사진 URL 도 prefill 해서
        // 다시 시작할 때 같은 reference 사용 가능.
        referenceImageUrl: design.referenceImageUrl || null,
        sourceName: design.name || styleLabel,
        ...planPrefill(design),
      },
    });
  };

  const styleLabel = styleName(design.style, design.spaceCategory);
  const lineOnly = design.outputType === 'plan_clean';
  const labels = planSliderLabels(t, design.outputType);
  const photoCount = Math.max(
    design.originalImageUrls?.length || 0,
    design.generatedImageUrls?.length || 0,
    1
  );

  const isLoggedIn = user && !user.isAnonymous;

  return (
    <div className="share-page">
      <main className="main">
        <GetTheAppBanner surface="share" />
        {/* Back 버튼: 모바일은 글로벌 MobileHeader 가 처리 (CSS 로 hide).
           데스크톱은 '← Back' 텍스트 버튼이 좌측에 노출됨. */}
        <div className="detail-topbar-row">
          {back.showBack ? (
            <button className="detail-back-btn" onClick={back.goBack} aria-label={t('back')}>
              <i className="material-symbols-rounded">arrow_back_ios_new</i>
              <span>{t('back')}</span>
            </button>
          ) : <span />}
          <div className="detail-actions">
            <button
              className={`like-btn ${liked ? 'liked' : ''}`}
              onClick={handleLike}
              aria-label="Like"
            >
              <i className="material-symbols-rounded">favorite</i>
              {likeCount > 0 && <span className="like-count">{likeCount}</span>}
            </button>
            <button
              className="detail-icon-btn"
              onClick={handleShare}
              title={t('share')}
              aria-label={t('share')}
            >
              <i className="material-symbols-rounded">ios_share</i>
            </button>
            {isLoggedIn && design.userId !== user.uid && (
              <button
                className="detail-icon-btn"
                onClick={() => setShowReport(true)}
                title={t('reportThisDesign')}
                aria-label={t('reportThisDesign')}
              >
                <i className="material-symbols-rounded">flag</i>
              </button>
            )}
          </div>
        </div>
        <div className="detail-title" style={{ marginBottom: '1rem' }}>
          <h2>{design.name || styleLabel}</h2>
          <p>{DesignService.formatDate(design.timestamp, lang)}{photoCount > 1 && ` · ${t('photoCount', { n: photoCount })}`}</p>
          {design.caption && (
            <p className="design-caption">{design.caption}</p>
          )}
        </div>
        {author?.handle && (
          <button
            type="button"
            className="feed-card-author"
            onClick={() => navigate(`/u/${author.handle}`)}
            style={{ marginBottom: '1.25rem' }}
          >
            {author.photoURL && (
              <img src={author.photoURL} alt="" className="feed-card-author-avatar" />
            )}
            <span className="feed-card-author-name">{author.handle}</span>
          </button>
        )}

        {/* Before/After pairs */}
        {Array.from({ length: photoCount }, (_, i) => {
          const before = design.originalImageUrls?.[i];
          const after = design.generatedImageUrls?.[i];
          return (
            <div key={i} className="result-photo-pair">
              {photoCount > 1 && <h3 className="result-photo-label">{t('photoLabel', { n: i + 1 })}</h3>}
              {before && after && design.outputType === 'plan_room' ? (
                <PlanRoomResult src={after} planSrc={design.roomOverlayUrl || before} alt={t('altGenerated', { n: i + 1 })} />
              ) : before && after ? (
                <BeforeAfterSlider
                  beforeSrc={before}
                  afterSrc={after}
                  beforeLabel={labels.before}
                  afterLabel={labels.after}
                  alt={t('altPhoto', { n: i + 1 })}
                  revealOnMount
                />
              ) : (
                <div className="image-comparison">
                  <div className="image-container">
                    <h3>{t('original')}</h3>
                    {before
                      ? <img src={before} alt={t('altOriginal', { n: i + 1 })} className="preview-image" />
                      : <div className="image-error"><p>{t('imageUnavailable')}</p></div>}
                  </div>
                  <div className="image-container">
                    <h3>{t('aiDesign')}</h3>
                    {after
                      ? <img src={after} alt={t('altGenerated', { n: i + 1 })} className="preview-image" />
                      : <div className="image-error"><p>{t('imageUnavailable')}</p></div>}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Style info */}
        <div className="preview-container">
          <h3>{t('styleUsed')}</h3>
          <div style={{ marginTop: '0.75rem' }}>
            <div className="style-tags">
              <button
                type="button"
                className="style-tag style-tag-link"
                onClick={() => navigate(`/feed?style=${encodeURIComponent(design.style)}${design.spaceCategory ? `&category=${design.spaceCategory}` : ''}`)}
                title={t('viewFeed')}
              >
                {styleLabel}
              </button>
              {design.spaceCategory && design.spaceCategory !== 'interior' && (
                <button
                  type="button"
                  className="style-tag style-tag-link"
                  onClick={() => navigate(`/feed?category=${design.spaceCategory}`)}
                  title={t('viewFeed')}
                >
                  {t('spaceCategories.' + design.spaceCategory)}
                </button>
              )}
              {design.outputType && (
                <span className="style-tag">{t(`planOutput_${design.outputType}`)}</span>
              )}
              {design.mode === 'staging' && design.detectedInputType !== 'floorplan' && (
                <span className="style-tag">{t('modes.staging')}</span>
              )}
              {design.detectedInputType && design.detectedInputType !== 'photo' && !design.outputType && (
                <span className="style-tag">{t('detectedInputTypes.' + design.detectedInputType)}</span>
              )}
              {design.exteriorOptions?.season && (
                <span className="style-tag">{t('seasons.' + design.exteriorOptions.season)}</span>
              )}
              {design.exteriorOptions?.timeOfDay && (
                <span className="style-tag">{t('timesOfDay.' + design.exteriorOptions.timeOfDay)}</span>
              )}
            </div>
            {design.customStyleInput && (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('customDescLabel')}</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{design.customStyleInput}</p>
              </div>
            )}
            {design.additionalRequest && (
              <div style={{ marginTop: '0.75rem' }}>
                <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{t('additionalReqLabel')}</p>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{design.additionalRequest}</p>
              </div>
            )}
          </div>
        </div>

        {/* Lang mismatch notice */}
        {design.lang && design.lang !== lang && (
          <div className="lang-notice">
            <i className="material-symbols-rounded">info</i>
            <span>{t('langMismatchNotice', { lang: LANG_LABELS[design.lang] || design.lang })}</span>
          </div>
        )}

        {/* Design analysis */}
        <div className="preview-container">
          <h3>{t('analysisTitle')}</h3>
          {design.spaceAnalysis && (
            <div><h4>{t('spaceAnalysis')}</h4><div className="md-content"><ReactMarkdown>{design.spaceAnalysis}</ReactMarkdown></div></div>
          )}
          {design.styleDescription && (
            <div><h4>{t('styleDescription')}</h4><div className="md-content"><ReactMarkdown>{design.styleDescription}</ReactMarkdown></div></div>
          )}
          {design.colorPalette && !lineOnly && (
            <div>
              <h4>{t('colorPalette')}</h4>
              <div className="color-palette">
                {design.colorPalette.map((color, i) => (
                  <div key={i} className="color-swatch-wrapper">
                    <div className="color-swatch" style={{ backgroundColor: color }} title={color} />
                    <span className="color-swatch-code">{color}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {design.furnitureRecommendations && !lineOnly && (
            <div>
              <h4>{t('furnitureRec')}</h4>
              <div className="recommendations-list md-content">
                <ul>
                  {design.furnitureRecommendations.map((item, i) => {
                    const shopUrl = buildShoppingUrl(item.searchQuery || item.item, lang);
                    return (
                      <li key={i}>
                        <strong>{item.item}</strong>
                        {shopUrl && (
                          <a
                            className="shop-link"
                            href={shopUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('shopFor', { item: item.item })}
                            onClick={() => logEvent(analytics, 'furniture_shop_clicked', { provider: shoppingProvider(lang) })}
                          >
                            <i className="material-symbols-rounded">shopping_bag</i>
                            <span>{t('shopShort')}</span>
                          </a>
                        )}
                        : <ReactMarkdown components={{ p: ({children}) => <>{children}</> }}>{item.description}</ReactMarkdown>
                        {item.placement && <> (<ReactMarkdown components={{ p: ({children}) => <>{children}</> }}>{item.placement}</ReactMarkdown>)</>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
          {design.lightingPlan && !lineOnly && (
            <div><h4>{t('lightingPlan')}</h4><div className="md-content"><ReactMarkdown>{design.lightingPlan}</ReactMarkdown></div></div>
          )}
          {design.tips && !lineOnly && (
            <div>
              <h4>{t('designTips')}</h4>
              <div className="recommendations-list md-content">
                <ul>{design.tips.map((tip, i) => <li key={i}><ReactMarkdown>{tip}</ReactMarkdown></li>)}</ul>
              </div>
            </div>
          )}
        </div>

        <Comments
          designId={id}
          designOwnerId={design.userId}
          user={user}
          onSignInRequest={openSignIn}
        />

        {/* CTA */}
        <div className="share-cta">
          {!isLoggedIn && <p>{t('shareViewCTA')}</p>}
          <div className="share-cta-btns">
            <button className="btn btn-primary" onClick={handleUseStyle}>
              <i className="material-symbols-rounded">replay</i>
              {t('useThisStyle')}
            </button>
            {/* 이 버튼도 게이트를 탄다. 옆의 "이 스타일로" 만 막아 두고
                여기를 열어 두면 게이트가 없는 것과 같다. */}
            {!isLoggedIn && (
              <button className="btn btn-secondary" onClick={handleCreateWithApp}>{t('createWithApp')}</button>
            )}
          </div>
        </div>

      </main>
      {showReport && (
        <ReportModal designId={id} user={user} onClose={() => setShowReport(false)} />
      )}
      <AppGateModal
        open={appGate.open}
        onClose={appGate.close}
        storeUrl={appGate.storeUrl}
        surface="share"
      />
    </div>
  );
}
